/* eslint-disable react/react-in-jsx-scope */
import IconExcel from "@/assets/icones/excel.svg?react";
import IconWord from "@/assets/icones/word.svg?react";
import ErrorBoundaryWrapper from "@/components/errors/ErrorBoundaryWrapper";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import { projectStatus } from "@/constants/constants";

import { useAuth } from "@/auth/hooks/useAuth";
import useScopedDashboardAccess from "@/auth/hooks/useScopedDashboardAccess";
import {
  PROJECT_QUERY_GC_TIME,
  PROJECT_QUERY_STALE_TIMES,
  projectQueryKeys,
} from "@/dashboard/projectQueryConfig";
import { DescriptifSkeleton } from "@/dashboard/components/project/components/descriptif/DescriptifSkeleton";
import HeaderDescriptif from "@/dashboard/components/project/components/descriptif/HeaderDescriptif";
import ReportingSkeleton from "@/dashboard/components/project/components/descriptif/ReportingSkeleton";
import TabPerformanceSkeleton from "@/dashboard/components/project/components/performance/TabPerformanceSkeleton";
import {
  buildProjectSyntheticExportSnapshot,
  type ProjectSyntheticExportSnapshot,
} from "@/dashboard/components/project/tabs/helpers/projectSyntheticExportSnapshot";
import {
  useModal,
  useProjectStore,
  useTabDashboard,
} from "@/dashboard/store/store";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { useDatabase } from "@/programs/hooks/useDatabase";
import { ProjectType } from "@/types/dashboard";
// 1. Import de useMemo
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { Skeleton } from "@/components/ui/skeleton";
import HeaderDescriptifSkeleton from "@/dashboard/components/project/components/descriptif/headerDescriptifSkeleton";
import usePostData from "@/hooks/usePostData";
import { useQueryClient } from "@tanstack/react-query";
import {
  CheckSquare,
  Download,
  FileSpreadsheet,
  FileText,
  Loader,
} from "lucide-react";
import {
  FormEvent,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AddProject from "./AddProject";

const loadReportingDetail = () =>
  import("@/dashboard/components/project/tabs/ReportingDetail").then(
    (module) => ({ default: module.default }),
  );
const loadDescriptionDetail = () =>
  import("@/dashboard/components/project/tabs/DescriptionDetail").then(
    (module) => ({ default: module.default }),
  );
const loadDescriptionDetailFullview = () =>
  import("@/dashboard/components/project/tabs/DescriptionDetailFullview").then(
    (module) => ({ default: module.default }),
  );
const loadPerformanceDetail = () =>
  import("@/dashboard/components/project/tabs/PerformanceDetail").then(
    (module) => ({ default: module.default }),
  );
const loadSummarySheet = () =>
  import("@/dashboard/components/project/tabs/SummarySheet").then((module) => ({
    default: module.default,
  }));
const loadProjectDocument = () =>
  import("@/dashboard/components/project/tabs/ProjectDocument").then(
    (module) => ({
      default: module.default,
    }),
  );

const ReportingDetail = lazy(loadReportingDetail);
const DescriptionDetail = lazy(loadDescriptionDetail);
const DescriptionDetailFullview = lazy(loadDescriptionDetailFullview);
const PerformanceDetail = lazy(loadPerformanceDetail);
const SummarySheet = lazy(loadSummarySheet);
const ProjectDocument = lazy(loadProjectDocument);

const preloadProjectTabs = () => {
  void Promise.allSettled([
    loadSummarySheet(),
    loadDescriptionDetail(),
    loadDescriptionDetailFullview(),
    loadPerformanceDetail(),
    loadReportingDetail(),
    loadProjectDocument(),
  ]);
};

const useDeferredTabActivation = (activeKey: string, delayMs = 80) => {
  const [readyTabs, setReadyTabs] = useState<Record<string, boolean>>(() => ({
    [activeKey]: true,
  }));

  useEffect(() => {
    if (delayMs <= 0) return;
    if (readyTabs[activeKey]) return;

    const timerId = window.setTimeout(() => {
      setReadyTabs((current) => ({
        ...current,
        [activeKey]: true,
      }));
    }, delayMs);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [activeKey, delayMs, readyTabs]);

  return delayMs <= 0 || readyTabs[activeKey] === true;
};

type ProjectExportModule =
  | "synthetic"
  | "descriptif"
  | "performance"
  | "reporting"
  | "documents";

const PROJECT_EXPORT_MODULE_OPTIONS: Array<{
  value: ProjectExportModule;
  title: string;
  description: string;
}> = [
  {
    value: "synthetic",
    title: "Fiche synthétique",
    description:
      "Les informations clés du projet, son contexte et ses repères principaux.",
  },
  {
    value: "descriptif",
    title: "Descriptif du projet",
    description:
      "Le contenu textuel du descriptif, les financements et les reponses du formulaire dynamique, hors images.",
  },
  {
    value: "performance",
    title: "Suivi de performance ESG",
    description:
      "Les indicateurs ESG du projet avec les valeurs prevues, realisees et verifiees par mois et par annee.",
  },
  {
    value: "reporting",
    title: "Reporting",
    description:
      "Les graphes de reporting du projet avec les series prevues, realisees et verifiees.",
  },
];

const sanitizeAsciiFilename = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7E]/g, "")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

const getFileNameFromDisposition = (
  disposition: string | null,
  fallback: string,
) => {
  const asciiFallback = sanitizeAsciiFilename(fallback);
  if (!disposition) return asciiFallback;
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const simpleMatch = disposition.match(/filename="?([^"]+)"?/i);
  return simpleMatch?.[1] ?? asciiFallback;
};

const isProjectForRoute = (
  project: ProjectType | null | undefined,
  routeProjectId?: string,
) => {
  if (!project?.id) return false;
  if (Number(project.id) !== Number(routeProjectId)) return false;
  if ((project as any).__optimistic) return false;

  return true;
};

const isCachedProjectReadyForRoute = (
  project: ProjectType | null | undefined,
  routeProjectId?: string,
) => {
  if (!isProjectForRoute(project, routeProjectId)) return false;

  return Boolean(
    project.program?.id &&
      project.type?.id &&
      "description" in project &&
      "manager" in project,
  );
};

const isProjectSummaryForRoute = (
  project: ProjectType | null | undefined,
  routeProjectId?: string,
) => {
  if (!project?.id) return false;
  return Number(project.id) === Number(routeProjectId);
};

const downloadProjectExcel = async (
  projectId: number,
  modules: ProjectExportModule[],
  syntheticSnapshot?: ProjectSyntheticExportSnapshot | null,
) => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");

  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/project/${projectId}/export/xlsx`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        modules,
        syntheticSnapshot: syntheticSnapshot ?? null,
      }),
    },
  );

  if (!response.ok) {
    throw new Error("Erreur lors de l'export Excel du projet");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getFileNameFromDisposition(
    response.headers.get("content-disposition"),
    `Rapport - Projet - export-${projectId}.xlsx`,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const downloadProjectWord = async (
  projectId: number,
  modules: ProjectExportModule[],
  syntheticSnapshot?: ProjectSyntheticExportSnapshot | null,
) => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");

  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/project/${projectId}/export/docx`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        modules,
        syntheticSnapshot: syntheticSnapshot ?? null,
      }),
    },
  );

  if (!response.ok) {
    throw new Error("Erreur lors de l'export Word du projet");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getFileNameFromDisposition(
    response.headers.get("content-disposition"),
    `Rapport - Projet - export-${projectId}.docx`,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const fetchProjectForExport = async (projectId: number) => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");

  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/project/${projectId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Erreur lors du chargement du projet pour l'export");
  }

  return response.json();
};

const ProjectExportDialog = ({
  isOpen,
  onOpenChange,
  isExporting,
  onExport,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isExporting: boolean;
  onExport: (
    format: "xlsx" | "docx",
    modules: ProjectExportModule[],
  ) => Promise<void>;
}) => {
  const [selectedFormat, setSelectedFormat] = useState<"xlsx" | "docx">("xlsx");
  const [selectedModules, setSelectedModules] = useState<ProjectExportModule[]>(
    PROJECT_EXPORT_MODULE_OPTIONS.map((option) => option.value),
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelectedFormat("xlsx");
    setSelectedModules(PROJECT_EXPORT_MODULE_OPTIONS.map((option) => option.value));
  }, [isOpen]);

  const toggleModule = (module: ProjectExportModule, checked: boolean) => {
    setSelectedModules((current) => {
      if (checked) {
        return current.includes(module) ? current : [...current, module];
      }

      return current.filter((item) => item !== module);
    });
  };

  const allModulesChecked =
    selectedModules.length === PROJECT_EXPORT_MODULE_OPTIONS.length;

  const toggleAllModules = (checked: boolean) => {
    setSelectedModules(
      checked
        ? PROJECT_EXPORT_MODULE_OPTIONS.map((option) => option.value)
        : [],
    );
  };

  const canExport = selectedModules.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-full min-w-0 max-w-[780px] rounded-[28px] border-[#d6e2d2] bg-[linear-gradient(180deg,#fbfdf9_0%,#f2f7ef_100%)] p-0 shadow-2xl sm:min-w-0">
        <div className="overflow-hidden rounded-[28px]">
          <div className="bg-[linear-gradient(135deg,#1f4b1d_0%,#5f8e53_60%,#94bf85_100%)] px-8 py-7 text-white">
            <div className="flex items-start justify-between gap-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
                  <Download className="h-3.5 w-3.5" />
                  Export du projet
                </div>
                <DialogTitle className="mt-4 text-[28px] font-semibold leading-tight text-white">
                  Exporter cette fiche projet
                </DialogTitle>
                <DialogDescription className="mt-2 max-w-[560px] text-sm leading-6 text-white/85">
                  Choisissez d’abord le format, puis les modules à inclure dans
                  le rapport. Tous les modules sont cochés par défaut et la
                  carte n’est pas exportée.
                </DialogDescription>
              </div>
              <div className="hidden rounded-[24px] border border-white/15 bg-white/10 p-4 md:block">
                <FileSpreadsheet className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>

          <div className="space-y-5 px-8 py-7">
            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => setSelectedFormat("xlsx")}
                className={`group relative flex h-full min-h-[220px] flex-col overflow-hidden rounded-[24px] border p-5 text-left transition-all ${
                  selectedFormat === "xlsx"
                    ? "border-[#6f9a5c] bg-white shadow-[0_18px_50px_rgba(75,107,60,0.15)] ring-2 ring-[#dfead9]"
                    : "border-[#d9e4d2] bg-white/80 hover:border-[#b7c9ae] hover:bg-white"
                }`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#2e5f22_0%,#8fb37f_100%)]" />
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-[18px] bg-[#edf6e9] p-3 text-[#2a5b1e]">
                    <IconExcel className="h-8 w-8" />
                  </div>
                </div>
                <div className="mt-5 flex flex-1 flex-col">
                  <h3 className="text-lg font-semibold text-[#24451f]">Excel</h3>
                  <p className="mt-2 text-sm leading-6 text-[#5d6e58]">
                    Export du projet au format Excel, structuré par modules pour
                    faciliter la lecture, l’analyse et le partage.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedFormat("docx")}
                className={`group relative flex h-full min-h-[220px] flex-col overflow-hidden rounded-[24px] border p-5 text-left transition-all ${
                  selectedFormat === "docx"
                    ? "border-[#8fa8d5] bg-white shadow-[0_18px_50px_rgba(69,99,143,0.15)] ring-2 ring-[#e3ebfb]"
                    : "border-[#d9e4d2] bg-white/80 hover:border-[#c6d6ef] hover:bg-white"
                }`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#45638f_0%,#9ab4de_100%)]" />
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-[18px] bg-[#eef4ff] p-3 text-[#45638f]">
                    <IconWord className="h-8 w-8" />
                  </div>
                </div>
                <div className="mt-5 flex flex-1 flex-col">
                  <h3 className="text-lg font-semibold text-[#314664]">Word</h3>
                  <p className="mt-2 text-sm leading-6 text-[#66758a]">
                    Rapport simplifié présentant les informations clés sous forme de texte, tableaux et synthèses.
                  </p>
                </div>
              </button>
            </div>

            <div className="rounded-[22px] border border-[#d7e3d2] bg-white/90 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-4">
                  <div className="rounded-[16px] bg-[#edf6e9] p-3 text-[#2a5b1e]">
                    <CheckSquare className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#294b24]">
                      Modules à inclure dans l’export
                    </p>
                    <p className="mt-1 text-sm leading-6 text-[#5f6d5c]">
                      Choisissez les sections du rapport projet à inclure dans
                      le fichier généré.
                    </p>
                  </div>
                </div>

                <label className="inline-flex items-center gap-3 rounded-full border border-[#d8e3d4] bg-[#f7faf5] px-4 py-2 text-sm font-medium text-[#3d5837]">
                  <Checkbox
                    checked={allModulesChecked}
                    onCheckedChange={(checked) => toggleAllModules(Boolean(checked))}
                    className="border-[#9bb58d] data-[state=checked]:border-[#2c5d20] data-[state=checked]:bg-[#2c5d20]"
                  />
                  Tout sélectionner
                </label>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {PROJECT_EXPORT_MODULE_OPTIONS.map((option) => {
                  const checked = selectedModules.includes(option.value);
                  return (
                    <label
                      key={option.value}
                      className={`flex cursor-pointer items-start gap-3 rounded-[20px] border px-4 py-4 transition-all ${
                        checked
                          ? "border-[#bfd3b5] bg-[#f7fbf5] shadow-[0_8px_24px_rgba(75,107,60,0.08)]"
                          : "border-[#e2e9de] bg-white hover:border-[#cad9c3]"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(nextChecked) =>
                          toggleModule(option.value, Boolean(nextChecked))
                        }
                        className="mt-0.5 border-[#9bb58d] data-[state=checked]:border-[#2c5d20] data-[state=checked]:bg-[#2c5d20]"
                      />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="text-sm font-semibold text-[#294b24]">
                          {option.title}
                        </span>
                        <span className="mt-1 text-sm leading-6 text-[#5f6d5c]">
                          {option.description}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-3 border-t border-[#dde7d8] bg-white/80 px-8 py-5 sm:justify-between sm:gap-3">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl border-[#cfdcc9] bg-white px-5 text-[#496045] hover:bg-[#f4f8f2]"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <div className="flex flex-1 items-center gap-4">
              {isExporting ? (
                <p className="flex-1 text-center text-sm leading-5 text-[#5f6d5c]">
                  L'export peut prendre un peu de temps, merci de patienter pendant la génération du fichier.
                </p>
              ) : null}
              <Button
                type="button"
                className="rounded-xl bg-[linear-gradient(135deg,#21511c_0%,#5f8f51_100%)] px-6 text-white shadow-lg hover:opacity-95"
                onClick={() => void onExport(selectedFormat, selectedModules)}
                disabled={isExporting || !canExport}
              >
                {isExporting ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Export en cours
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Exporter
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DashboardProject = () => {
  const [isEditable, setIsEditable] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isExportingProject, setIsExportingProject] = useState(false);
  const lastSynchronizedProjectRef = useRef<ProjectType | null>(null);
  const { id } = useParams<{ id: string }>();
  const {
    project: currentProject,
    setIsProjectApiReady,
    setProject,
    projectExportContextById,
  } = useProjectStore();
  const { user } = useAuth();
  const { tab, setTab } = useTabDashboard();
  const { postData: notifySoldProject } = usePostData<any>();
  const queryClient = useQueryClient();
  const { t } = useTranslation("dashboard", {
    keyPrefix: "project",
  });
  const isActiveTabReady = useDeferredTabActivation(tab, 0);
  const [isProjectDetailFetchEnabled, setIsProjectDetailFetchEnabled] =
    useState(false);

  useEffect(() => {
    const timerId = window.setTimeout(preloadProjectTabs, 150);

    return () => {
      window.clearTimeout(timerId);
    };
  }, []);

  const {
    data: projectSummary,
    error: projectSummaryError,
  } = useFetchDataV2<any>(
    id ? `${import.meta.env.VITE_API_ENDPOINT}/project/${id}/summary` : "",
    {
      cacheId: id ? projectQueryKeys.summary(id) : "project-summary",
      staleTime: PROJECT_QUERY_STALE_TIMES.summary,
      gcTime: PROJECT_QUERY_GC_TIME,
      browserCache: true,
      browserCacheNamespace: "project-summary",
      browserCacheTtlMs: 1000 * 60 * 60,
    },
  );

  useEffect(() => {
    setIsProjectDetailFetchEnabled(true);
  }, [id, tab]);

  // 2. Logique dynamique pour l'URL et la clé de cache
  const { apiUrl, cacheKey } = useMemo(() => {
    const baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/project/${id}`;
    let excludes = "indicator,reporting"; // Défaut pour 'descriptif'

    switch (tab) {
      case "suivi-perf":
        excludes = "reporting";
        break;
      case "reporting":
        excludes = "";
        break;
      // \! on laisse le défaut pour les autres onglets
    }

    const cacheVersion = "v2";
    const queryString = excludes ? `?excludes=${excludes}` : "";
    return {
      apiUrl: `${baseUrl}${queryString}`,
      cacheKey: `project-${id}-${tab}-${cacheVersion}`, // Clé unique par onglet
    };
  }, [tab, id]); // Dépendances: recalculer si l'onglet ou l'ID changent
  // 3. Appel à useFetchDataV2 avec les valeurs dynamiques
  const visibleProjectSummary = isProjectSummaryForRoute(projectSummary, id)
    ? (projectSummary as ProjectType)
    : null;
  const isProjectSummaryResolved =
    Boolean(visibleProjectSummary) || Boolean(projectSummaryError);
  const effectiveApiUrl =
    isProjectSummaryResolved &&
    isActiveTabReady &&
    isProjectDetailFetchEnabled
      ? apiUrl
      : "";
  const { data, loading, isApiReady } = useFetchDataV2<ProjectType>(effectiveApiUrl, {
    cacheId: cacheKey,
    staleTime: PROJECT_QUERY_STALE_TIMES.detail,
    gcTime: PROJECT_QUERY_GC_TIME,
    browserCache: true,
    browserCacheNamespace: "project-tabs",
    browserCacheTtlMs: 1000 * 60 * 60,
  });

  const cachedRouteProject = useMemo(() => {
    if (!cacheKey || !apiUrl) return null;
    return (
      queryClient.getQueryData<ProjectType>([cacheKey, apiUrl]) ??
      queryClient.getQueryData<ProjectType>([`project-${id}-suivi-perf-v2`, `${import.meta.env.VITE_API_ENDPOINT}/project/${id}?excludes=reporting`]) ??
      queryClient.getQueryData<ProjectType>([`project-${id}-reporting-v2`, `${import.meta.env.VITE_API_ENDPOINT}/project/${id}`]) ??
      queryClient.getQueryData<ProjectType>([`project-${id}-descriptif-v2`, `${import.meta.env.VITE_API_ENDPOINT}/project/${id}?excludes=indicator,reporting`]) ??
      null
    );
  }, [apiUrl, cacheKey, id, queryClient]);
  const [lastStableProject, setLastStableProject] =
    useState<ProjectType | null>(null);
  const readyData = isProjectForRoute(data, id) ? data : null;
  const readyCachedRouteProject = isCachedProjectReadyForRoute(cachedRouteProject, id)
    ? cachedRouteProject
    : null;
  const readyLastStableProject = isCachedProjectReadyForRoute(lastStableProject, id)
    ? lastStableProject
    : null;
  const hydrateProjectScope = useCallback(
    (project: ProjectType | null) => {
      if (!project?.id || project?.program?.carrier?.id) {
        return project;
      }

      const programId = Number(project?.program?.id ?? 0);
      const programSnapshots = queryClient.getQueriesData({
        exact: false,
        queryKey: ["programsInfo"],
      });
      let matchedProgram: any = null;

      for (const [, snapshot] of programSnapshots) {
        if (!Array.isArray(snapshot)) continue;

        matchedProgram =
          snapshot.find((program: any) => Number(program?.id ?? 0) === programId) ??
          snapshot.find((program: any) =>
            Array.isArray(program?.projects) &&
            program.projects.some(
              (item: any) => Number(item?.id ?? 0) === Number(project.id),
            ),
          );

        if (matchedProgram) break;
      }

      if (!matchedProgram?.carrier?.id) {
        return project;
      }

      return {
        ...project,
        program: {
          ...project.program,
          carrier: matchedProgram.carrier,
        },
      };
    },
    [queryClient],
  );
  const scopedReadyData = useMemo(
    () => hydrateProjectScope(readyData),
    [hydrateProjectScope, readyData],
  );
  const scopedCachedRouteProject = useMemo(
    () => hydrateProjectScope(readyCachedRouteProject),
    [hydrateProjectScope, readyCachedRouteProject],
  );
  const scopedLastStableProject = useMemo(
    () => hydrateProjectScope(readyLastStableProject),
    [hydrateProjectScope, readyLastStableProject],
  );
  const visibleProject =
    scopedReadyData ?? scopedCachedRouteProject ?? scopedLastStableProject;
  const visibleHeaderProject = visibleProject ?? visibleProjectSummary;
  const visibleOwnerCarrierId =
    visibleProject?.program?.carrier?.id ??
    visibleProjectSummary?.program?.carrier?.id ??
    0;
  const hidePostActions = useHidePostActions(visibleOwnerCarrierId);
  const { actsAsSubcontractorForScope } = useScopedDashboardAccess(
    visibleOwnerCarrierId,
  );

  useEffect(() => {
    setIsProjectApiReady(
      Boolean(visibleProject) || !effectiveApiUrl || isApiReady,
    );
  }, [
    effectiveApiUrl,
    isApiReady,
    setIsProjectApiReady,
    visibleProject?.id,
  ]);

  const [searchParams, setSearchParams] = useSearchParams();
  const { setOpenModal, modals } = useModal();
  const { updateProgramInDB } = useDatabase();
  const navigate = useNavigate();
  const [tabInitializedForProjectId, setTabInitializedForProjectId] =
    useState<string | null>(null);

  const { updateProjectInDB } = useDatabase();
  const stepFromUrlRaw = searchParams.get("step");

  useEffect(() => {
    if (!id || tab === "document") {
      return;
    }

    sessionStorage.removeItem(`embedded-project-document-init:${id}`);
  }, [id, tab]);

  const handleChangeTab = (
    value:
      | "synthetic"
      | "descriptif"
      | "suivi-perf"
      | "reporting"
      | "document",
  ) => {
    if (tab !== value) {
      setTab(value);
    }
    const params = new URLSearchParams(searchParams);
    if (params.get("tab") !== value) {
      params.set("tab", value);
      setSearchParams(params, { preventScrollReset: true });
    }
  };

  useEffect(() => {
    const validTabs = [
      "synthetic",
      "descriptif",
      "suivi-perf",
      "reporting",
      "document",
    ];
    const tabFromUrlRaw = searchParams.get("tab");
    const tabFromUrl = tabFromUrlRaw === "perf" ? "suivi-perf" : tabFromUrlRaw;
    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      const nextTab = tabFromUrl as
        | "synthetic"
        | "descriptif"
        | "suivi-perf"
        | "reporting"
        | "document";
      if (tab !== nextTab) {
        setTab(nextTab);
      }
      return;
    }

    if (tabFromUrlRaw) {
      if (tab !== "descriptif") {
        setTab("descriptif");
      }
      const params = new URLSearchParams(searchParams);
      if (params.get("tab") !== "descriptif") {
        params.set("tab", "descriptif");
        setSearchParams(params, { preventScrollReset: true });
      }
    }
  }, [searchParams, setSearchParams, setTab, tab]);

  // Définit l'onglet par défaut une fois par projet, sans écraser un onglet URL.
  useEffect(() => {
    if (!id || !visibleHeaderProject?.id) return;
    if (tabInitializedForProjectId === id) return;
    if (searchParams.get("tab")) {
      setTabInitializedForProjectId(id);
      return;
    }

    const defaultTab =
      Number(visibleHeaderProject.status) === projectStatus.Validated
        ? "synthetic"
        : "descriptif";

    if (tab !== defaultTab) {
      setTab(defaultTab);
    }

    const params = new URLSearchParams(searchParams);
    params.set("tab", defaultTab);
    setSearchParams(params, { preventScrollReset: true });
    setTabInitializedForProjectId(id);
  }, [
    id,
    searchParams,
    setSearchParams,
    setTab,
    tab,
    tabInitializedForProjectId,
    visibleHeaderProject?.id,
    visibleHeaderProject?.status,
  ]);


  // ====== Logique "ajout projet" (inchangée) ======
  const handleSubmit = useCallback(
    async (
      e: FormEvent<HTMLFormElement>,
      projectsTemp: any,
      projectsTempBdd: any,
    ) => {
      e.preventDefault();
      window.scrollTo(0, 0);
      const projectsArray: any[] = [];
      projectsTemp.map((project: any) => {
        if (project.type) {
          projectsArray.push({
            name: project.name,
            type: project.type,
          });
        }
      });
      const projectsArrayBdd: any[] = [];
      projectsTempBdd?.map((project: any) => {
        if (project.type) {
          projectsArrayBdd.push({
            name: project.name,
            type: project.type,
          });
        }
      });

      const program = await updateProgramInDB(
        { projects: [...projectsArrayBdd] },
        Number(data?.program.id),
      );
      toast({
        title: t("Vos informations sont bien prises en compte."),
      });
      setOpenModal({ name: "project", value: false });
      if (actsAsSubcontractorForScope) {
        // window.location.reload();
        window.location.href = `/dashboard/program/${program?.id}`;
      }
      if (program) {
        window.location.href = `/dashboard/project/${program?.projects[program?.projects.length - 1].id
          }`;
      }
    },
    [updateProgramInDB, data?.program?.id, setOpenModal, navigate, t],
  );

  const emptyDataPost = {
    name: "",
    description: "",
    type: 0,
    expectedDuration: 0,
    launchDate: "",
    objective: "",
    actions: "",
    benefits: "",
    criteria: "",
    manager: {
      email: "",
      firstname: "",
      lastname: "",
      logo: { id: 0, name: "", type: 0, uri: "" },
      society: "",
      phone: "",
    },
    country: 0,
    plantedSpecies: [],
    protectedSpecies: [],
    peopleInvolved: 0,
    fundRecipient: { email: "", name: "" },
    fundLocal: { email: "", name: "" },
    fundLocalSubcontractors: [],
    fundLocalSubcontractorUsers: [],
    fundLocalSubcontractorIds: [],
    fundLocalSubcontractorId: null,
    fundLocalSubcontractorAccesses: [],
    fundLocalSubcontractorAccessIds: [],
    fundLocalSubcontractorGiveAccess: false,
    trees: 0,
    surface: 0,
    sau: "0",
    linear: "0",
    location: [],
    parcel: [],
  };
  const [postData, setPostData] = useState(emptyDataPost);

  const tabsConfig = useMemo(
    () => [
      { value: "synthetic" as const, label: t("Fiche Synthétique") },
      { value: "descriptif" as const, label: t("Descriptif du projet") },
      { value: "suivi-perf" as const, label: t("Suivi des indicateurs") },
      { value: "reporting" as const, label: t("Reporting") },
      { value: "document", label: t("Documents liés") },
    ],
    [t],
  );

  const syntheticSuspenseFallback = <SyntheticTabSkeleton />;
  const tabSuspenseFallback = <FicheBase />;
  const descriptifSuspenseFallback = <DescriptifSkeleton />;
  const performanceSuspenseFallback = <TabPerformanceSkeleton />;
  const reportingSuspenseFallback = <ReportingSkeleton />;
  const documentSuspenseFallback = <DocumentTabSkeleton />;
  const activeTabSuspenseFallback =
    tab === "synthetic"
      ? syntheticSuspenseFallback
      : tab === "descriptif"
      ? descriptifSuspenseFallback
      : tab === "suivi-perf"
        ? performanceSuspenseFallback
        : tab === "reporting"
          ? reportingSuspenseFallback
          : tab === "document"
            ? documentSuspenseFallback
            : tabSuspenseFallback;
  const tabContentClassName = "mb-4 animate-in fade-in-50 duration-150";

  const projectTabsList = (
    <TabsList className="mb-2 grid w-full grid-cols-5 bg-background ">
      {tabsConfig.map((tabItem) => (
        <TabsTrigger
          aria-label={tabItem.label}
          key={tabItem.value}
          value={tabItem.value}
          className={`rounded-none border-b-2 text-muted-foreground data-[state=active]:border-foreground data-[state=active]:shadow-none ${
            tabItem.value !== "descriptif" &&
            tabItem.value !== "suivi-perf" &&
            tabItem.value !== "document" &&
            visibleHeaderProject?.status != 3 &&
            visibleHeaderProject?.status != 5
              ? "pointer-events-none cursor-context-menu opacity-20"
              : ""
          }`}
        >
          {tabItem.label}
        </TabsTrigger>
      ))}
    </TabsList>
  );

  useEffect(() => {
    if (isProjectForRoute(data, id)) {
      setLastStableProject(hydrateProjectScope(data));
    }
  }, [data, hydrateProjectScope, id]);

  useEffect(() => {
    if (scopedReadyData) {
      setPostData({
        ...emptyDataPost,
        ...scopedReadyData,
      });
      localStorage.setItem("currency_verdeo", scopedReadyData?.country?.id || 0);
    }
  }, [scopedReadyData]);

  useEffect(() => {
    if (user && scopedReadyData) {
      if (
        actsAsSubcontractorForScope &&
        scopedReadyData?.creator?.id !== user.id &&
        scopedReadyData?.status === 3
      ) {
        setCanEdit(false);
      } else {
        setCanEdit(true);
      }
    }
  }, [user, scopedReadyData, actsAsSubcontractorForScope]);

  useEffect(() => {
    if (!visibleProject) return;

    const stepFromUrl = Number(stepFromUrlRaw);
    const currentProjectMatches =
      Number(currentProject?.id) === Number(visibleProject.id);
    const currentStoreStep = currentProjectMatches
      ? Number(currentProject?.step ?? 1)
      : 1;
    const nextStep = Math.max(
      Number(visibleProject?.step ?? 1),
      currentStoreStep,
      Number.isFinite(stepFromUrl) ? stepFromUrl : 1,
    );
    const shouldMarkSoldNotification =
      isApiReady &&
      visibleProject?.soldStatus === 2 &&
      visibleProject?.notifSold !== true &&
      (!currentProjectMatches || currentProject?.notifSold !== true);
    const nextNotifSold =
      shouldMarkSoldNotification ||
      (currentProjectMatches && currentProject?.notifSold === true)
        ? true
        : visibleProject?.notifSold;
    const storeIsAlreadySynchronized =
      currentProjectMatches &&
      Number(currentProject?.step ?? 1) === nextStep &&
      currentProject?.notifSold === nextNotifSold;
    const sourceProjectChanged =
      lastSynchronizedProjectRef.current !== visibleProject;

    if (shouldMarkSoldNotification) {
      const url = `${import.meta.env.VITE_IP_API}/api/project/edit/${visibleProject?.id
        }`;
      void notifySoldProject({
        url,
        data: {
          notifSold: true,
        },
      });
    }

    if (sourceProjectChanged || !storeIsAlreadySynchronized) {
      lastSynchronizedProjectRef.current = visibleProject;
      setProject({
        ...visibleProject,
        step: nextStep,
        notifSold: nextNotifSold,
      });
    }
  }, [
    currentProject?.id,
    currentProject?.notifSold,
    currentProject?.step,
    isApiReady,
    notifySoldProject,
    setProject,
    stepFromUrlRaw,
    visibleProject,
  ]);

  useEffect(() => {
    if (!isApiReady) return;
    if (!scopedReadyData?.id) return;

    const isPersonalized = Boolean(
      (scopedReadyData as any)?.isPersonnalized ??
      (scopedReadyData as any)?.personnalized ??
      (scopedReadyData as any)?.is_personnalized ??
      false,
    );
    const statusNumber = Number(scopedReadyData?.status);
    const shouldOpenDynamicForm =
      isPersonalized &&
      Number.isFinite(statusNumber) &&
      statusNumber !== 3;

    if (!shouldOpenDynamicForm) return;

    const params = new URLSearchParams(searchParams);
    params.set("projectId", String(scopedReadyData.id));
    if (scopedReadyData?.program?.id) {
      params.set("programId", String(scopedReadyData.program.id));
    }

    navigate(`/dashboard/project/create-custom?${params.toString()}`, {
      replace: true,
    });
  }, [isApiReady, scopedReadyData?.id, scopedReadyData?.program?.id, scopedReadyData?.status, navigate, searchParams]);

  if (!visibleProject)
    return (
      <>
        {visibleHeaderProject ? (
          <HeaderDescriptif
            project={visibleHeaderProject}
            projectSummary={visibleProjectSummary}
            hasDetailedProject={Boolean(visibleProject)}
          />
        ) : (
          <HeaderDescriptifSkeleton />
        )}
        <Tabs value={tab} onValueChange={handleChangeTab} className="w-full">
          {projectTabsList}
        </Tabs>
        {tab === "synthetic" ? (
          <SyntheticTabSkeleton />
        ) : tab === "descriptif" ? (
          <DescriptifSkeleton />
        ) : tab === "reporting" ? (
          <ReportingSkeleton />
        ) : tab === "document" ? (
          <DocumentTabSkeleton />
        ) : (
          <TabPerformanceSkeleton />
        )}
      </>
    );
  return (
    <div>
      {visibleHeaderProject && (
        <HeaderDescriptif
          project={visibleHeaderProject}
          projectSummary={visibleProjectSummary}
          hasDetailedProject={Boolean(visibleProject)}
        />
      )}

      <div className="mb-5 mt-4 flex justify-end">
        <Button
          type="button"
          onClick={() => setIsExportDialogOpen(true)}
          className="rounded-[16px] border border-[#d2dfcd] bg-[linear-gradient(180deg,#fbfdf9_0%,#f1f6ee_100%)] px-5 py-2.5 text-[#285120] shadow-[0_10px_28px_rgba(57,94,46,0.08)] transition-all hover:border-[#2c5d20] hover:bg-[#2c5d20] hover:text-white"
        >
          <Download className="mr-2 h-4 w-4" />
          Exporter
        </Button>
      </div>

      <Tabs value={tab} onValueChange={handleChangeTab} className="w-full">
        {projectTabsList}

        {tab == "synthetic" && (
          <TabsContent key="synthetic" value="synthetic" className={tabContentClassName}>
            <Suspense fallback={activeTabSuspenseFallback}>
              <ErrorBoundaryWrapper>
                {isActiveTabReady ? <SummarySheet /> : activeTabSuspenseFallback}
              </ErrorBoundaryWrapper>
            </Suspense>
          </TabsContent>
        )}

        {tab == "descriptif" && (
          <TabsContent key="descriptif" value="descriptif" className={tabContentClassName}>
            <Suspense fallback={activeTabSuspenseFallback}>
              <ErrorBoundaryWrapper>
                {isActiveTabReady ? (
                  hidePostActions ||
                    ((visibleProject?.status === 3 || visibleProject?.status === 5) && !isEditable) ? (
                    <DescriptionDetailFullview
                      postData={postData}
                      setPostData={setPostData}
                      setIsEditable={setIsEditable}
                      isEditable={isEditable}
                      setShowAlert={setShowAlert}
                      showAlert={showAlert}
                    />
                  ) : (
                    <DescriptionDetail
                      postData={postData}
                      setPostData={setPostData}
                      setIsEditable={setIsEditable}
                      isEditable={isEditable}
                      setShowAlert={setShowAlert}
                      showAlert={showAlert}
                    />
                  )
                ) : (
                  activeTabSuspenseFallback
                )}
              </ErrorBoundaryWrapper>
            </Suspense>
          </TabsContent>
        )}

        {tab == "suivi-perf" && (
          <TabsContent key="suivi-perf" value="suivi-perf" className={tabContentClassName}>
            <Suspense fallback={activeTabSuspenseFallback}>
              <ErrorBoundaryWrapper>
                {isActiveTabReady ? (
                  <PerformanceDetail />
                ) : (
                  activeTabSuspenseFallback
                )}
              </ErrorBoundaryWrapper>
            </Suspense>
          </TabsContent>
        )}

        {tab == "reporting" && (
          <TabsContent key="reporting" value="reporting" className={tabContentClassName}>
            <Suspense fallback={activeTabSuspenseFallback}>
              <ErrorBoundaryWrapper>
                {isActiveTabReady &&
                  (visibleProject?.status === 3 || visibleProject?.status === 5) ? (
                    <ReportingDetail isFetching={loading && !visibleProject} />
                  ) : (
                    activeTabSuspenseFallback
                  )}
              </ErrorBoundaryWrapper>
            </Suspense>
          </TabsContent>
        )}
        {tab == "document" && (
          <TabsContent key="document" value="document" className={tabContentClassName}>
            <Suspense fallback={activeTabSuspenseFallback}>
              <ErrorBoundaryWrapper>
                {isActiveTabReady ? (
                  <ProjectDocument
                    id={id}
                    programId={visibleProject?.program?.id ?? visibleProjectSummary?.program?.id}
                  />
                ) : (
                  activeTabSuspenseFallback
                )}
              </ErrorBoundaryWrapper>
            </Suspense>
          </TabsContent>
        )}
      </Tabs>

      {modals["project"] && <AddProject onHandleSubmit={handleSubmit} />}
      <ProjectExportDialog
        isOpen={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
        isExporting={isExportingProject}
        onExport={async (format, modules) => {
          if (!data?.id) return;
          if (modules.length === 0) {
            toast({
              title: "Aucun module sélectionné",
              description:
                "Choisissez au moins un module avant de lancer l’export.",
              variant: "destructive",
            });
            return;
          }

          try {
            setIsExportingProject(true);
            let syntheticSnapshot: ProjectSyntheticExportSnapshot | null = null;

            if (modules.includes("synthetic")) {
              const projectForExport =
                Array.isArray(data?.indicators) && data.indicators.length > 0
                  ? data
                  : await fetchProjectForExport(data.id);
              syntheticSnapshot =
                await buildProjectSyntheticExportSnapshot(
                  projectForExport,
                  projectExportContextById[data.id] ?? null,
                );
            }

            if ("docx" === format) {
              await downloadProjectWord(data.id, modules, syntheticSnapshot);
            } else {
              await downloadProjectExcel(data.id, modules, syntheticSnapshot);
            }
            setIsExportDialogOpen(false);
            toast({
              title: "Export généré",
              description:
                "Le rapport projet a bien été généré et téléchargé.",
            });
          } catch (_error) {
            toast({
              title: "Impossible de générer l’export",
              description:
                "Une erreur est survenue pendant la génération du fichier Excel du projet.",
              variant: "destructive",
            });
          } finally {
            setIsExportingProject(false);
          }
        }}
      />
    </div>
  );
};

export default DashboardProject;

const SyntheticTabSkeleton = () => {
  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={index}
            className="h-[108px] rounded-[20px] bg-[#ecedee]"
          />
        ))}
      </div>
      <div className="rounded-[20px] bg-[#F7F9FB] p-5">
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-[220px] rounded-[18px] bg-[#ecedee]" />
          <Skeleton className="h-[220px] rounded-[18px] bg-[#ecedee]" />
        </div>
      </div>
    </div>
  );
};

const DocumentTabSkeleton = () => {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Skeleton className="h-10 w-48 rounded-[12px] bg-[#ecedee]" />
        <Skeleton className="h-10 w-40 rounded-[12px] bg-[#ecedee]" />
        <Skeleton className="h-10 w-40 rounded-[12px] bg-[#ecedee]" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton
            key={index}
            className="h-[182px] rounded-[20px] bg-[#ecedee]"
          />
        ))}
      </div>
    </div>
  );
};

const FicheBase = () => {
  return (
    <div className="mt-4">
      <div className="flex flex-row gap-6 h-auto">
        {/* Image Skeleton */}
        <div className="basis-1/4 h-[180px]">
          <div className="rounded-[20px] overflow-hidden bg-[#f4f4f4] h-full">
            <Skeleton height="100%" />
          </div>
        </div>

        {/* Text Section Skeleton */}
        <div className="h-30 basis-2/4 space-y-4">
          <div className="flex flex-row justify-between items-start">
            <div className="w-2/3">
              <Skeleton height={24} width="80%" />
            </div>
            <Skeleton circle height={32} width={32} />
          </div>

          <div className="space-y-2">
            <Skeleton count={3} />
          </div>

          <Skeleton width={80} height={16} />
        </div>

        {/* Funding Info Skeleton */}
        <div className="grid grid-rows-4 gap-2 max-h-70 rounded-[20px] p-4 basis-1/4 bg-[#F7F9FB]">
          {/* Currency + montant */}
          <div className="space-y-2">
            <Skeleton width={83} height={30} />
            <Skeleton width="60%" height={20} />
            <Skeleton width="100%" height={8} />
          </div>

          {/* Pie Chart Placeholder */}
          <div className="h-[100px] w-full">
            <Skeleton height="100%" />
          </div>

          {/* % Contributions */}
          <div>
            <Skeleton width="40%" height={16} />
          </div>

          {/* Date & Duration */}
          <div className="grid grid-cols-2 gap-2">
            <Skeleton height={16} />
            <Skeleton height={16} />
          </div>
        </div>
      </div>
    </div>
  );
};
